import React, { useEffect, useRef, useState } from 'react';
import OrderDesign from '../OrderDesign';

/**
 * RepositioningOverlay - Simple overlay that renders a unit copy and animates it
 * Renders outside zones in a fixed overlay, animates from old to new position, then disappears
 */
export const RepositioningOverlay = ({ reposition, unit, gameState, userId }) => {
  const overlayRef = useRef(null);
  const [positions, setPositions] = useState({ start: null, end: null });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [currentPosition, setCurrentPosition] = useState({ left: 0, top: 0 });
  const animationStartTimeRef = useRef(null);
  const animationFrameRef = useRef(null);

  // ✅ CRITICAL FIX: Calculate isPlayerUnit outside useEffect so it's available in render
  // Normalize both to strings for comparison (playerId might be string or number)
  const normalizedUserId = String(userId);
  const normalizedRepositionPlayerId = reposition ? String(reposition.playerId) : '';
  const isPlayerUnit = normalizedRepositionPlayerId === normalizedUserId;

  useEffect(() => {
    if (!reposition || !unit || !gameState || !userId) return;

    console.log('🎯 OVERLAY: Player comparison', {
      repositionPlayerId: reposition.playerId,
      normalizedRepositionPlayerId,
      userId,
      normalizedUserId,
      isPlayerUnit,
      fromZone: reposition.fromZone,
      toZone: reposition.toZone
    });
    
    // Find the actual DOM elements to get their screen positions
    const findElementPosition = (playerId, instanceId, zone, index) => {
      // ✅ CRITICAL FIX: Use data-slot-zone and data-slot-index to find the exact slot element
      // Slot elements have: data-slot-zone="your_front" or "opponent_front", data-slot-index="0-4"
      // ✅ CRITICAL FIX: Normalize both to strings for comparison
      const normalizedPlayerId = String(playerId);
      const isThisPlayerUnit = normalizedPlayerId === normalizedUserId;
      const slotZoneName = isThisPlayerUnit 
        ? `your_${zone}` 
        : `opponent_${zone}`;
      
      // ✅ PRIMARY METHOD: Find slot element by data-slot-zone and data-slot-index
      // This is the most reliable way to find the exact slot position
      const slotSelector = `[data-slot-zone="${slotZoneName}"][data-slot-index="${index}"]`;
      const slotElement = document.querySelector(slotSelector);
      
      if (slotElement) {
        const rect = slotElement.getBoundingClientRect();
        const position = {
          left: rect.left + window.scrollX,
          top: rect.top + window.scrollY,
          width: rect.width || 180,
          height: rect.height || 120
        };
        console.log(`✅ OVERLAY: Found slot element for ${slotZoneName}[${index}]`, {
          selector: slotSelector,
          rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
          position: position
        });
        return position;
      } else {
        console.warn(`⚠️ OVERLAY: Slot element not found: ${slotSelector}`);
      }
      
      // ✅ FALLBACK 1: Try to find by data-unit-key using instanceId
      if (instanceId) {
        const key = `${playerId}_${instanceId}`;
        const unitElement = document.querySelector(`[data-unit-key="${key}"]`);
        if (unitElement) {
          // Find the parent slot element
          const parentSlot = unitElement.closest('[data-slot-zone][data-slot-index]');
          if (parentSlot) {
            const rect = parentSlot.getBoundingClientRect();
            return {
              left: rect.left + window.scrollX,
              top: rect.top + window.scrollY,
              width: rect.width || 180,
              height: rect.height || 120
            };
          }
          // If no parent slot, use unit element itself
          const rect = unitElement.getBoundingClientRect();
          return {
            left: rect.left + window.scrollX,
            top: rect.top + window.scrollY,
            width: rect.width || 180,
            height: rect.height || 120
          };
        }
      }
      
      // ✅ FALLBACK 2: Find by zone container and calculate position
      const zoneSelector = isThisPlayerUnit 
        ? `.battle-zone.${zone}` 
        : `.battle-zone.opponent-${zone}`;
      const zoneContainer = document.querySelector(zoneSelector);
      
      if (zoneContainer) {
        // Try to find slot by index within zone
        const slots = Array.from(zoneContainer.querySelectorAll('[data-slot-zone][data-slot-index]'));
        const targetSlot = slots.find(slot => {
          const slotIndex = parseInt(slot.getAttribute('data-slot-index'));
          const slotZone = slot.getAttribute('data-slot-zone');
          return slotIndex === index && slotZone === slotZoneName;
        });
        
        if (targetSlot) {
          const rect = targetSlot.getBoundingClientRect();
          return {
            left: rect.left + window.scrollX,
            top: rect.top + window.scrollY,
            width: rect.width || 180,
            height: rect.height || 120
          };
        }
        
        // Last resort: calculate position based on zone container
        const zoneRect = zoneContainer.getBoundingClientRect();
        const unitWidth = 180;
        const positionInZone = index * unitWidth;
        return {
          left: zoneRect.left + window.scrollX + positionInZone,
          top: zoneRect.top + window.scrollY,
          width: 180,
          height: 120
        };
      }

      // ✅ FALLBACK 3: Final fallback calculation if nothing found
      console.warn(`⚠️ OVERLAY: Could not find slot element for ${slotZoneName}[${index}], using fallback calculation`);
      return {
        left: 0,
        top: 0,
        width: 180,
        height: 120
      };
    };

    // ✅ FIX: Use requestAnimationFrame to wait for DOM to update after game state change
    // This ensures React has finished rendering before we try to find slot positions
    const findPositions = () => {
      // ✅ CRITICAL: Find start position from OLD slot (unit is no longer there, but slot still exists)
      // Find end position from NEW slot (unit is now there)
      const startPos = findElementPosition(reposition.playerId, reposition.instanceId, reposition.fromZone, reposition.fromIndex);
      const endPos = findElementPosition(reposition.playerId, reposition.instanceId, reposition.toZone, reposition.toIndex);

      // Calculate center positions for accurate animation
      const startCenterX = startPos.left + (startPos.width / 2);
      const startCenterY = startPos.top + (startPos.height / 2);
      const endCenterX = endPos.left + (endPos.width / 2);
      const endCenterY = endPos.top + (endPos.height / 2);
      const calculatedOffsetX = endCenterX - startCenterX;
      const calculatedOffsetY = endCenterY - startCenterY;

      console.log('🎯 OVERLAY: Positions calculated', {
        playerId: reposition.playerId,
        userId: userId,
        isPlayerUnit: isPlayerUnit,
        fromZone: reposition.fromZone,
        fromIndex: reposition.fromIndex,
        toZone: reposition.toZone,
        toIndex: reposition.toIndex,
        start: startPos,
        end: endPos,
        startCenter: { x: startCenterX, y: startCenterY },
        endCenter: { x: endCenterX, y: endCenterY },
        offsetX: calculatedOffsetX,
        offsetY: calculatedOffsetY
      });

      // ✅ VALIDATION: Ensure positions are valid before setting
      // Check that positions are valid numbers (not NaN, not 0,0 unless that's actually the position)
      const isValidPosition = (pos) => {
        return pos && 
               typeof pos.left === 'number' && !isNaN(pos.left) &&
               typeof pos.top === 'number' && !isNaN(pos.top) &&
               typeof pos.width === 'number' && !isNaN(pos.width) &&
               typeof pos.height === 'number' && !isNaN(pos.height) &&
               pos.width > 0 && pos.height > 0;
      };

      if (isValidPosition(startPos) && isValidPosition(endPos)) {
        console.log('✅ OVERLAY: Valid positions, setting state', { startPos, endPos });
        setPositions({ start: startPos, end: endPos });
      } else {
        console.warn('⚠️ OVERLAY: Invalid positions calculated, retrying...', { 
          startPos, 
          endPos,
          startValid: isValidPosition(startPos),
          endValid: isValidPosition(endPos)
        });
        // Retry after next frame
        requestAnimationFrame(() => {
          setTimeout(() => {
            const retryStartPos = findElementPosition(reposition.playerId, reposition.instanceId, reposition.fromZone, reposition.fromIndex);
            const retryEndPos = findElementPosition(reposition.playerId, reposition.instanceId, reposition.toZone, reposition.toIndex);
            if (isValidPosition(retryStartPos) && isValidPosition(retryEndPos)) {
              console.log('✅ OVERLAY: Retry successful', { retryStartPos, retryEndPos });
              setPositions({ start: retryStartPos, end: retryEndPos });
            } else {
              console.error('❌ OVERLAY: Retry failed, positions still invalid', { 
                retryStartPos, 
                retryEndPos,
                startValid: isValidPosition(retryStartPos),
                endValid: isValidPosition(retryEndPos)
              });
            }
          }, 200);
        });
      }
    };

    // Wait for next frame to ensure DOM has updated
    let timeoutId = null;
    const rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(findPositions, 50);
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [reposition, unit, gameState, userId]);

  // ✅ FIX: Animate using requestAnimationFrame for precise control
  useEffect(() => {
    if (!positions.start || !positions.end || animationComplete) return;

    // Calculate center-to-center positions
    const startCenterX = positions.start.left + (positions.start.width / 2);
    const startCenterY = positions.start.top + (positions.start.height / 2);
    const endCenterX = positions.end.left + (positions.end.width / 2);
    const endCenterY = positions.end.top + (positions.end.height / 2);
    
    const startLeft = startCenterX - (positions.start.width / 2);
    const startTop = startCenterY - (positions.start.height / 2);
    const endLeft = endCenterX - (positions.start.width / 2);
    const endTop = endCenterY - (positions.start.height / 2);

    // Set initial position
    setCurrentPosition({ left: startLeft, top: startTop });
    animationStartTimeRef.current = Date.now();

    const duration = 2000; // 2 seconds
    const startX = startLeft;
    const startY = startTop;
    const deltaX = endLeft - startLeft;
    const deltaY = endTop - startTop;

    const animate = () => {
      const elapsed = Date.now() - animationStartTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease in-out function
      const easeInOut = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      const currentX = startX + (deltaX * easeInOut);
      const currentY = startY + (deltaY * easeInOut);

      setCurrentPosition({ left: currentX, top: currentY });

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete
        setCurrentPosition({ left: endLeft, top: endTop });
        setTimeout(() => {
          setAnimationComplete(true);
        }, 100);
      }
    };

    // Start animation after a brief delay to ensure DOM is ready
    const startTimeout = setTimeout(() => {
      animationFrameRef.current = requestAnimationFrame(animate);
    }, 50);

    return () => {
      clearTimeout(startTimeout);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [positions.start, positions.end, animationComplete]);

  // ✅ FIX: Don't render if animation is complete or if reposition/unit is missing
  if (!reposition || !unit || !positions.start || !positions.end || animationComplete) {
    return null;
  }

  // Match the unit's canonical slot in gameState (unit is read from toZone/toIndex).
  // Using fromZone here made unitCannotInitiateAttack count the wrong row (e.g. id 240
  // full-zone rule), which showed zZz on the flying copy even when the unit could attack.
  const zonePrefix = isPlayerUnit ? 'your' : 'opponent';
  const zoneName = `${zonePrefix}_${reposition.toZone}`;

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        left: `${currentPosition.left}px`,
        top: `${currentPosition.top}px`,
        width: `${positions.start.width}px`,
        height: `${positions.start.height}px`,
        zIndex: 100000, // Very high z-index to ensure it's above everything
        pointerEvents: 'none',
        transition: 'none', // Disable CSS transitions to use our animation
        willChange: 'left, top', // Optimize for animation
        isolation: 'isolate', // Create new stacking context
      }}
    >
      <OrderDesign
        order={unit}
        minimal={true}
        zoneName={zoneName}
        slotIndex={reposition.toIndex}
        gameState={gameState}
        userId={userId}
      />
    </div>
  );
};

